import { supabase } from '../lib/supabase'
import type { CourseApiDto } from '../types'
import type Post from '../types/post'

interface StatsRow {
    id: number
    course_date_start: string
}

type RequestPayload = string | Record<string, unknown>
type ApiResponse<T> = { data: T }
type MutationResult = { success: true; uuid?: string; id?: number }
type ChangedRow = { uuid: string } | { id: number }
type MutationAction = 'save' | 'delete'

const mutationError = (action: MutationAction, code?: string, missingRow = false): Error => {
    const summary = action === 'save' ? 'Uložení se nepodařilo potvrdit.' : 'Smazání se nepodařilo potvrdit.'

    if (missingRow || code === 'PGRST116') {
        return new Error(`${summary} Záznam už neexistuje nebo nemáte oprávnění ho změnit. Obnovte stránku a zkontrolujte údaje.`)
    }

    if (code === '42501' || code === 'PGRST301' || code === 'PGRST302' || code === 'PGRST303') {
        return new Error(`${summary} Přihlaste se znovu. Pokud potíže přetrvají, požádejte správce o kontrolu oprávnění.`)
    }

    return new Error(`${summary} Zkontrolujte připojení, obnovte stránku a ověřte, zda se změna projevila.`)
}

// RLS can turn a mutation into a successful response with no changed rows.
// Only acknowledge a save/delete when the database returns the affected identity.
const confirmMutation = async <T extends ChangedRow>(
    request: PromiseLike<{ data: T | null; error: { code?: string } | null }>,
    action: MutationAction,
    expectedIdentity?: string | number,
): Promise<T> => {
    let result

    try {
        result = await request
    } catch {
        throw mutationError(action)
    }

    if (result.error) {
        throw mutationError(action, result.error.code)
    }

    const row = result.data
    const identity = row && ('uuid' in row ? row.uuid : row.id)
    if (
        !row ||
        !identity ||
        (expectedIdentity !== undefined && identity !== expectedIdentity)
    ) {
        throw mutationError(action, undefined, true)
    }

    return row
}

const parsePayload = <T>(payload: RequestPayload): T => {
    if (typeof payload === 'string') {
        return JSON.parse(payload) as T
    }

    return payload as T
}

const requireData = <T>(data: T | null, error: Error | null): T => {
    if (error) {
        throw error
    }

    if (data === null) {
        throw new Error('No data returned from Supabase')
    }

    return data
}

const parseDeletePath = (path: string) => {
    const [resource, ...queryParts] = path.split('&')
    const params = new URLSearchParams(queryParts.join('&'))

    return {
        resource,
        uuid: params.get('uuid'),
    }
}

const api = {
    async get<T>(path: string): Promise<ApiResponse<T>> {
        switch (path) {
            case 'posts': {
                const { data, error } = await supabase.from('posts').select('*').order('created', { ascending: false })
                return { data: requireData(data as Post[] | null, error) as unknown as T }
            }
            case 'stats': {
                const { data, error } = await supabase.from('info').select('*').order('id', { ascending: true }).limit(1)
                return { data: requireData(data as StatsRow[] | null, error) as unknown as T }
            }
            case 'courses': {
                const { data, error } = await supabase.from('courses').select('*').order('sort_order', { ascending: true })
                return { data: requireData(data as CourseApiDto[] | null, error) as unknown as T }
            }
            default:
                throw new Error(`Unsupported GET path: ${path}`)
        }
    },

    async put<T = { success: true; uuid: string }>(path: string, payload: RequestPayload): Promise<ApiResponse<T>> {
        switch (path) {
            case 'posts': {
                const post = parsePayload<{ author?: string; title: string; message: string }>(payload)
                const row = await confirmMutation<{ uuid: string }>(supabase
                    .from('posts')
                    .insert({
                        author_uuid: post.author ?? 'authenticated-user',
                        title: post.title,
                        description: post.message,
                    })
                    .select('uuid')
                    .single(), 'save')
                return { data: { success: true, uuid: row.uuid } as unknown as T }
            }
            case 'courses': {
                const course = parsePayload<Omit<CourseApiDto, 'uuid' | 'created_at' | 'updated_at'>>(payload)
                const row = await confirmMutation<{ uuid: string }>(supabase
                    .from('courses')
                    .insert({
                        title: course.title,
                        subtitle: course.subtitle,
                        sub_subtitle: course.sub_subtitle,
                        duration: course.duration,
                        price: course.price,
                        short_description: course.short_description,
                        description: course.description,
                        icon_type: course.icon_type,
                        custom_image_url: course.custom_image_url,
                        small_text: course.small_text ? 1 : 0,
                        sort_order: Number(course.sort_order ?? 0),
                    })
                    .select('uuid')
                    .single(), 'save')
                return { data: { success: true, uuid: row.uuid } as unknown as T }
            }
            default:
                throw new Error(`Unsupported PUT path: ${path}`)
        }
    },

    async post<T = MutationResult>(path: string, payload: RequestPayload): Promise<ApiResponse<T>> {
        switch (path) {
            case 'stats': {
                const stats = parsePayload<{ course_date_start: string }>(payload)
                const row = await confirmMutation<{ id: number }>(supabase
                    .from('info')
                    .upsert({ id: 1, course_date_start: stats.course_date_start })
                    .select('id')
                    .single(), 'save', 1)
                return { data: { success: true, id: row.id } as unknown as T }
            }
            case 'posts': {
                const post = parsePayload<{ uuid: string; title: string; message: string }>(payload)
                const row = await confirmMutation<{ uuid: string }>(supabase
                    .from('posts')
                    .update({ title: post.title, description: post.message })
                    .eq('uuid', post.uuid)
                    .select('uuid')
                    .single(), 'save', post.uuid)
                return { data: { success: true, uuid: row.uuid } as unknown as T }
            }
            case 'courses': {
                const course = parsePayload<CourseApiDto>(payload)
                const row = await confirmMutation<{ uuid: string }>(supabase
                    .from('courses')
                    .update({
                        title: course.title,
                        subtitle: course.subtitle,
                        sub_subtitle: course.sub_subtitle,
                        duration: course.duration,
                        price: course.price,
                        short_description: course.short_description,
                        description: course.description,
                        icon_type: course.icon_type,
                        custom_image_url: course.custom_image_url,
                        small_text: course.small_text ? 1 : 0,
                        sort_order: Number(course.sort_order ?? 0),
                    })
                    .eq('uuid', course.uuid)
                    .select('uuid')
                    .single(), 'save', course.uuid)
                return { data: { success: true, uuid: row.uuid } as unknown as T }
            }
            default:
                throw new Error(`Unsupported POST path: ${path}`)
        }
    },

    async delete<T = { success: true; uuid: string }>(path: string): Promise<ApiResponse<T>> {
        const { resource, uuid } = parseDeletePath(path)

        if (!uuid) {
            throw new Error('Missing uuid in delete path')
        }

        switch (resource) {
            case 'posts': {
                const row = await confirmMutation<{ uuid: string }>(supabase
                    .from('posts').delete().eq('uuid', uuid).select('uuid').single(), 'delete', uuid)
                return { data: { success: true, uuid: row.uuid } as unknown as T }
            }
            case 'courses': {
                const row = await confirmMutation<{ uuid: string }>(supabase
                    .from('courses').delete().eq('uuid', uuid).select('uuid').single(), 'delete', uuid)
                return { data: { success: true, uuid: row.uuid } as unknown as T }
            }
            default:
                throw new Error(`Unsupported DELETE path: ${path}`)
        }
    },
}

export default api
